import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from 'sonner';

export type ManagedUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  subscription: string;
  joinDate: string;
  lastActive: string;
};

export const useUserManagement = () => {
  // Instead of trying to use the admin API (which requires service role),
  // we'll use the standard data API to fetch users from the profiles table
  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ['managed-users'],
    queryFn: async () => {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Fetch user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');
      
      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        throw rolesError;
      }

      // Fetch subscriptions
      const { data: subscriptions, error: subsError } = await supabase
        .from('subscriptions')
        .select('*');
      
      if (subsError) {
        console.error('Error fetching subscriptions:', subsError);
        throw subsError;
      }

      // Since we can't directly access auth.users, we'll fetch user emails from auth based on profiles
      // Get user emails - we'll do this for users individually
      const managedUsers: ManagedUser[] = await Promise.all(
        profiles.map(async (profile) => {
          // Try to get user data (like email) based on user ID
          // Note: This won't work with standard permissions but we'll include it in case
          // admin rights are provided in the future
          let email = '';
          let lastSignIn = null;
          let createdAt = null;
          
          // Get email from user_metadata in another table if available
          // Otherwise, just use "User {id}" as placeholder
          
          const roleData = userRoles?.find(r => r.user_id === profile.id);
          const subscription = subscriptions?.find(s => s.user_id === profile.id);
          
          return {
            id: profile.id,
            name: profile.full_name || 'Unnamed User',
            email: email || `user-${profile.id.substring(0, 8)}@example.com`,
            role: roleData?.role || 'basic',
            subscription: subscription?.plan || 'basic',
            joinDate: profile.created_at ? format(new Date(profile.created_at), 'yyyy-MM-dd') : 'Unknown',
            lastActive: lastSignIn ? format(new Date(lastSignIn), 'yyyy-MM-dd') : 'Never'
          };
        })
      );

      return managedUsers;
    }
  });

  const handleDeleteUser = async (userId: string) => {
    try {
      // First delete from profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
      
      if (profileError) throw profileError;
      
      // Note: We can't delete from auth.users without admin rights
      // so this is as much as we can do with standard permissions
      
      toast.success('User profile deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleEditUser = async (userId: string) => {
    // This is a placeholder for edit functionality
    toast.info('Edit user functionality coming soon');
  };

  return {
    users,
    isLoading,
    handleDeleteUser,
    handleEditUser
  };
};
